/**
 * CrmTodayView — the unified daily worklist (Phase 2 of the "always-on engine" work): every Lead
 * and Opportunity that needs attention right now, merged into one urgency-sorted queue via
 * services/crmToday.js. The new default landing tab for the CRM workspace — the "someone manning
 * the shop opens this every morning" surface. Clicking a row opens that record's own existing
 * detail modal (CrmLeadDetailModal / CrmOpportunityDetailModal) — no new modal built for this.
 */
import { useEffect, useState } from 'react'
import { getTodayQueue } from '../../services/crmToday.js'
import { URGENCY } from './crmLeadNextStep.js'
import CrmLeadDetailModal from './CrmLeadDetailModal.jsx'
import CrmOpportunityDetailModal from './CrmOpportunityDetailModal.jsx'

export default function CrmTodayView() {
  const [queue, setQueue] = useState(null)
  const [openLead, setOpenLead] = useState(null)
  const [openOpp, setOpenOpp] = useState(null)

  // Rows here are queue ENTRIES ({ type, id, record }), so the open modals re-sync against each
  // entry's own record rather than the queue array — same intent as crmSync.js#syncOpen, which
  // does not apply directly to this shape. A record that drops out of the queue (it no longer
  // needs attention) keeps its current value rather than blanking the open modal.
  function syncRecord(queue, type) {
    return cur => {
      if (!cur || !cur.id || !Array.isArray(queue)) return cur
      const e = queue.find(x => x.type === type && x.record && x.record.id === cur.id)
      return (e && e.record) || cur
    }
  }
  function load() {
    setQueue(null)
    getTodayQueue().then(q => {
      setQueue(q)
      setOpenLead(syncRecord(q, 'lead'))
      setOpenOpp(syncRecord(q, 'opportunity'))
    }).catch(() => setQueue([]))
  }
  useEffect(() => { load() }, [])

  const redCount = queue ? queue.filter(e => e.step.urgency === 'red').length : 0
  const amberCount = queue ? queue.filter(e => e.step.urgency === 'amber').length : 0

  return (
    <div style={S.wrap}>
      <div style={S.head}>
        <div style={S.headTitle}>Today</div>
        <div style={S.headSub}>
          {queue === null ? 'Loading…'
            : queue.length === 0 ? 'Nothing needs attention right now — the pipeline is caught up.'
            : `${queue.length} item${queue.length === 1 ? '' : 's'} need attention` +
              (redCount ? ` · ${redCount} overdue` : '') + (amberCount ? ` · ${amberCount} due soon` : '')}
        </div>
      </div>

      <div style={S.list}>
        {queue === null ? <div style={S.muted}>Loading…</div>
          : queue.length === 0 ? <div style={S.empty}>Nothing needs attention right now — the pipeline is caught up.</div>
          : queue.map(e => {
            const u = URGENCY[e.step.urgency] || URGENCY.amber
            return (
              <button key={e.type + ':' + e.id} style={{ ...S.row, background: u.bg, borderLeft: `3px solid ${u.accent}` }}
                onClick={() => (e.type === 'lead' ? setOpenLead(e.record) : setOpenOpp(e.record))}>
                <span style={{ ...S.typeBadge, color: u.accent, borderColor: u.border }}>{e.type === 'lead' ? 'Lead' : 'Opp'}</span>
                <span style={S.rowMain}>
                  <span style={S.rowName}>{e.record.name}</span>
                  <span style={S.rowStepTitle}>{e.step.title}</span>
                </span>
                <span style={{ ...S.urgencyLabel, color: u.accent }}>{u.label}</span>
              </button>
            )
          })}
      </div>

      {openLead && <CrmLeadDetailModal lead={openLead} onClose={() => setOpenLead(null)} onSaved={load} />}
      {openOpp && <CrmOpportunityDetailModal opp={openOpp} onClose={() => setOpenOpp(null)} onChanged={load} />}
    </div>
  )
}

const S = {
  wrap: { maxWidth: 780, margin: '0 auto', padding: '18px 20px 60px' },
  head: { marginBottom: 16 },
  headTitle: { fontFamily: '"Montserrat", sans-serif', fontSize: 20, fontWeight: 300, color: 'var(--aj-text)', marginBottom: 4 },
  headSub: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: 'var(--aj-text-mid)' },
  list: { display: 'flex', flexDirection: 'column', gap: 8 },
  row: { display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', border: '0.5px solid var(--aj-border)', borderRadius: 8, padding: '11px 14px', cursor: 'pointer' },
  typeBadge: { fontFamily: '"DM Sans", sans-serif', fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', border: '0.5px solid', borderRadius: 4, padding: '2px 6px', flexShrink: 0 },
  rowMain: { display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, gap: 1 },
  rowName: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  rowStepTitle: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'var(--aj-text-mid)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  urgencyLabel: { fontFamily: '"DM Sans", sans-serif', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', flexShrink: 0 },
  muted: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: 'var(--aj-text-mid)', fontStyle: 'italic', padding: '20px 2px' },
  empty: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text-mid)', fontStyle: 'italic', padding: '30px 14px', textAlign: 'center', border: '0.5px solid var(--aj-border)', borderRadius: 10 },
}
